"use client"

import { MediaSelect } from "@/(admin)/modules/media/pages/SelectMediaUIBuilder"
import { ComponentConfig } from "@measured/puck"
import { HeroBannerLayoutProps } from "../../../../../../../types/types"
import { RenderConfig } from "./RenderConfig"

export const HeroBannerLayout: ComponentConfig<HeroBannerLayoutProps> = {
  label: "Tamda Media | Hero Banner Layout",
  fields: {
    // Swiper Configuration - Choose Yes to use multiple slides, No to use single background
    swiperConfig: {
      label: "Swiper Configuration",
      type: "object",
      objectFields: {
        enabled: {
          label: "Enable Swiper (Yes = Multiple Slides, No = Single Background)",
          type: "radio",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
        },
        autoplay: {
          label: "Autoplay",
          type: "select",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
        },
        autoplayDelay: {
          label: "Autoplay Delay (ms)",
          type: "number",
          min: 0,
        },
        loop: {
          label: "Loop",
          type: "select",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
        },
        navigation: {
          label: "Navigation Arrows",
          type: "select",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
        },
        pagination: {
          label: "Pagination Dots",
          type: "select",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
        },
        spaceBetween: {
          label: "Space Between Slides (px)",
          type: "number",
          min: 0,
        },
        speed: {
          label: "Transition Speed (ms)",
          type: "number",
          min: 0,
        },
      },
    },
    // Multiple slides mode - Use these fields when Swiper is enabled (Yes)
    slides: {
      label: "Slides (Only used when Swiper is enabled)",
      type: "array",
      getItemSummary: (slide: any, index = 0) => `Slide ${index + 1}: ${slide?.mainText || "No text"}`,
      arrayFields: {
        background: {
          label: "Background",
          type: "object",
          objectFields: {
            image: {
              label: "Background Image",
              type: "custom",
              render: (props: any) => <MediaSelect {...props} />,
            },
            height: {
              label: "Background Height",
              type: "text",
            },
          },
        },
        topText: {
          label: "Top Text",
          type: "text",
        },
        mainText: {
          label: "Main Text",
          type: "text",
        },
        mainTextStyles: {
          label: "Main Text Styles",
          type: "object",
          objectFields: {
            fontSize: {
              label: "Font Size",
              type: "text",
            },
            fontWeight: {
              label: "Font Weight",
              type: "text",
            },
            lineHeight: {
              label: "Line Height",
              type: "text",
            },
          },
        },
        listButtons: {
          label: "List Buttons",
          type: "array",
          arrayFields: {
            buttonName: {
              label: "Button Name",
              type: "text",
            },
            buttonType: {
              label: "Button Type",
              type: "select",
              options: [
                { label: "Primary", value: "primary" },
                { label: "Outline", value: "outline" },
              ],
            },
            buttonLink: {
              label: "Button Link",
              type: "text",
            },
            buttonBg: {
              label: "Button Background",
              type: "text",
            },
            buttonSize: {
              label: "Button Size (e.g., width: 200px; height: 50px)",
              type: "text",
            },
            buttonFontSize: {
              label: "Button Font Size",
              type: "text",
            },
            buttonFontWeight: {
              label: "Button Font Weight",
              type: "text",
            },
            buttonLineHeight: {
              label: "Button Line Height",
              type: "text",
            },
            hoverBackgroundColor: {
              label: "Hover Background Color",
              type: "text",
            },
            hoverColor: {
              label: "Hover Text Color",
              type: "text",
            },
            hoverBorder: {
              label: "Hover Border (e.g., 2px solid #fff)",
              type: "text",
            },
            hoverTransform: {
              label: "Hover Transform (e.g., scale(1.1))",
              type: "text",
            },
            hoverOpacity: {
              label: "Hover Opacity (0-1)",
              type: "text",
            },
            hoverBoxShadow: {
              label: "Hover Box Shadow (e.g., 0 4px 8px rgba(0,0,0,0.2))",
              type: "text",
            },
          },
        },
      },
    },
    // Single slide mode - Use these fields when Swiper is disabled (No)
    background: {
      label: "Background",
      type: "object",
      objectFields: {
        image: {
          label: "Background Image",
          type: "custom",
          render: (props: any) => <MediaSelect {...props} />,
        },
        height: {
          label: "Background Height",
          type: "text",
        },
      },
    },
    // Keep legacy backgroundImage for backward compatibility
    backgroundImage: {
      label: "Background Image (Legacy)",
      type: "custom",
      render: (props: any) => <MediaSelect {...props} />,
    },
    topText: {
      label: "Top Text",
      type: "text",
    },
    mainText: {
      label: "Main Text",
      type: "text",
    },
    mainTextStyles: {
      label: "Main Text Styles",
      type: "object",
      objectFields: {
        fontSize: {
          label: "Font Size",
          type: "text",
        },
        fontWeight: {
          label: "Font Weight",
          type: "text",
        },
        lineHeight: {
          label: "Line Height",
          type: "text",
        },
      },
    },
    listButtons: {
      label: "List Buttons",
      type: "array",
      arrayFields: {
        buttonName: {
          label: "Button Name",
          type: "text",
        },
        buttonType: {
          label: "Button Type",
          type: "select",
          options: [
            { label: "Primary", value: "primary" },
            { label: "Outline", value: "outline" },
          ],
        },
        buttonLink: {
          label: "Button Link",
          type: "text",
        },
        buttonBg: {
          label: "Button Background",
          type: "text",
        },
        buttonSize: {
          label: "Button Size (e.g., width: 200px; height: 50px)",
          type: "text",
        },
        buttonFontSize: {
          label: "Button Font Size",
          type: "text",
        },
        buttonFontWeight: {
          label: "Button Font Weight",
          type: "text",
        },
        buttonLineHeight: {
          label: "Button Line Height",
          type: "text",
        },
        hoverBackgroundColor: {
          label: "Hover Background Color",
          type: "text",
        },
        hoverColor: {
          label: "Hover Text Color",
          type: "text",
        },
        hoverBorder: {
          label: "Hover Border (e.g., 2px solid #fff)",
          type: "text",
        },
        hoverTransform: {
          label: "Hover Transform (e.g., scale(1.1))",
          type: "text",
        },
        hoverOpacity: {
          label: "Hover Opacity (0-1)",
          type: "text",
        },
        hoverBoxShadow: {
          label: "Hover Box Shadow (e.g., 0 4px 8px rgba(0,0,0,0.2))",
          type: "text",
        },
      },
    },
    buttonStyles: {
      label: "Button Styles",
      type: "object",
      objectFields: {
        size: {
          label: "Button Size (e.g., width: 200px; height: 50px)",
          type: "text",
        },
        fontSize: {
          label: "Font Size",
          type: "text",
        },
        fontWeight: {
          label: "Font Weight",
          type: "text",
        },
        lineHeight: {
          label: "Line Height",
          type: "text",
        },
        gap: {
          label: "Gap Between Buttons",
          type: "text",
        },
      },
    },
    className: {
      label: "Class Name",
      type: "text",
    },
    // Legacy fields for backward compatibility
    mainTextFontSize: {
      label: "Main Text Font Size (Legacy)",
      type: "text",
    },
    mainTextFontWeight: {
      label: "Main Text Font Weight (Legacy)",
      type: "text",
    },
    mainTextLineHeight: {
      label: "Main Text Line Height (Legacy)",
      type: "text",
    },
    backgroundHeight: {
      label: "Background Height (Legacy)",
      type: "text",
    },
    buttonSize: {
      label: "Button Size (Legacy)",
      type: "text",
    },
    buttonFontSize: {
      label: "Button Font Size (Legacy)",
      type: "text",
    },
    buttonFontWeight: {
      label: "Button Font Weight (Legacy)",
      type: "text",
    },
    buttonLineHeight: {
      label: "Button Line Height (Legacy)",
      type: "text",
    },
    buttonGap: {
      label: "Button Gap (Legacy)",
      type: "text",
    },
  },
  defaultProps: {
    swiperConfig: {
      enabled: false,
      autoplay: true,
      autoplayDelay: 3000,
      loop: true,
      navigation: true,
      pagination: true,
      spaceBetween: 0,
      speed: 600,
    },
    slides: [],
    background: {
      image: "",
      height: undefined,
    },
    backgroundImage: "",
    topText: "TAMDA GROUP",
    mainText: "ĐỒNG HÀNH ĐỂ THÀNH CÔNG",
    mainTextStyles: {
      fontSize: undefined,
      fontWeight: undefined,
      lineHeight: undefined,
    },
    listButtons: [
      {
        buttonName: "Dịch vụ",
        buttonType: "primary",
        buttonLink: "/services",
        buttonBg: "#18AF4E",
      },
    ],
    buttonStyles: {
      size: undefined,
      fontSize: undefined,
      fontWeight: undefined,
      lineHeight: undefined,
      gap: undefined,
    },
    className: "",
  },
  ...RenderConfig,
}
